'use client';
import React, { useEffect, useRef, useState } from 'react';
import { LocaleDetails } from 'shared/src/types/languages.type';
import dynamic from 'next/dynamic';
import autosize from 'autosize';
import { SearchSpinner } from '@/components/search-bar/animation/search-spinner';
import { ThreeDotsLoading } from '@/components/loading/three-dots-loading';
import { ClientInfo } from 'shared/src/types/article.type';
import { processHtmlContent } from './process-html-content';
import '@/styles/search.css';

// dynamicly import Demo from './Demo';
const SearchDemoArticle = dynamic(() => import('./search-demo').then((module) => module.SearchDemoArticle));

type SearchBarProps = {
  locale: LocaleDetails;
  info?: ClientInfo;
  title?: string;
  clientId: string;
  author?: string;
};

const SearchBar = ({ locale, info, title = '', clientId, author = '' }: SearchBarProps) => {
  const [searchTerm, setSearchTerm] = useState(title);
  const [isDemo, setIsDemo] = useState(false);
  const [enterPressed, setEnterPressed] = useState(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isLoadingModal, setIsLoadingModal] = useState<boolean>(false);
  const [isHtmlContent, setIsHtmlContent] = useState<boolean>(false);
  const [htmlPreview, setHtmlPreview] = useState<string>('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [height, setHeight] = useState('2.4rem');

  useEffect(() => {
    setIsSearching(true);

    // Check if content is HTML
    const htmlPattern = /<div[^>]*class[^>]*markdown[^>]*>|<h[1-6][^>]*data-start[^>]*>|<p[^>]*data-start[^>]*>/i;
    const isHtml = htmlPattern.test(searchTerm);
    setIsHtmlContent(isHtml);

    if (isHtml) {
      // Extract and clean HTML for preview
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = searchTerm;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      const preview = textContent.substring(0, 200) + (textContent.length > 200 ? '...' : '');
      setHtmlPreview(preview);
    } else {
      setHtmlPreview('');
    }

    if (inputRef.current) {
      inputRef.current.focus();
      autosize.update(inputRef.current);
      setHeight(inputRef.current?.style.height);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (inputRef.current) {
      autosize(inputRef.current);
    }
    if (buttonRef.current) {
      autosize(buttonRef.current);
    }
    const inputElement = inputRef.current;
    return () => {
      if (inputElement) {
        autosize.destroy(inputElement);
      }
    };
  }, []);

  const pressSearch = async (isNewSearch = false) => {
    if (searchTerm.length > 0 || isNewSearch) {
      setIsDemo(false);
      setEnterPressed(true);
      setIsLoadingModal(true);

      try {
        let response;
        let processedContent = searchTerm;

        if (isHtmlContent) {
          // Process HTML content first using server action (algorithmic approach for speed)
          await processHtmlContent({
            htmlContent: searchTerm,
            useAI: false, // Use algorithmic approach
            clientId,
            locale,
            author,
          });

          // Redirect directly to the created article
          setSearchTerm('');
          setEnterPressed(false);
          setIsSearching(false);
          setIsLoadingModal(false);
          setIsHtmlContent(false);
          setHtmlPreview('');

          return; // Exit early since we handled everything
        } else {
          // Use regular subject-based search endpoint
          response = await fetch('https://inceptionai.app.n8n.cloud/webhook/create-new-article', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              clientId: clientId,
              subject: searchTerm,
              author: author,
              lang: locale.languageCode || 'en',
            }),
          });
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('Webhook response:', result);

        setSearchTerm('');
        setEnterPressed(false);
        setIsSearching(false);
        setIsLoadingModal(false);
        setIsHtmlContent(false);
        setHtmlPreview('');

        window.location.href = `/${locale.path}/articles/${result.id}`;
      } catch (error) {
        console.error('Error calling webhook:', error);
        setSearchTerm(' ');
        setEnterPressed(false);
        setIsSearching(false);
        setIsLoadingModal(false);
        setIsHtmlContent(false);
        setHtmlPreview('');
        await new Promise((resolve) => setTimeout(resolve, 50));
        setSearchTerm('');
        alert('Error while creating new article');
      }
    }
  };

  return (
    <>
      <div className={`z-10 ${isDemo && !isSearching ? 'mb-32' : 'mb-0'}`}>
        <div className={`relative flex w-full items-center space-x-2 rounded-lg text-base`}>
          <textarea
            ref={inputRef}
            className={`flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm transition-all placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-blue-400 ${isHtmlContent ? 'h-32' : ''} ${isSearching ? 'rounded-r-none' : ''}`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                pressSearch();
              }
            }}
            onFocus={async () => {
              if (searchTerm.length !== 0) {
                setIsSearching(true);
              } else {
                setIsDemo(true);
              }
            }}
            onBlur={async () => {
              if (searchTerm.length === 0) setIsSearching(false);
              await new Promise((resolve) => setTimeout(resolve, 300));
              setIsDemo(false);
            }}
            placeholder={
              isHtmlContent ? 'HTML content detected - Ready to create article from deep search...' : 'Ready to write?'
            }
            style={{
              minHeight: `2.5rem`,
              resize: 'none',
            }}
          />
          <SearchSpinner enterPressed={enterPressed} />
          {isHtmlContent && searchTerm && !enterPressed && (
            <div className="absolute top-full left-0 right-0 mt-2 p-4 bg-blue-50 border border-blue-200 rounded-lg shadow-lg dark:bg-blue-900/20 dark:border-blue-800 z-20">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">HTML Deep Search Content Detected</h4>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                    This will create an article from the pasted deep search HTML content.
                  </p>
                  <div className="text-xs text-blue-600 dark:text-blue-400 bg-white/50 dark:bg-black/20 p-2 rounded border">
                    <strong>Preview:</strong> {htmlPreview}
                  </div>
                </div>
              </div>
            </div>
          )}
          {
            <button
              ref={buttonRef}
              className={`flex h-10 items-center justify-center rounded-lg bg-blue-500 px-4 text-white transition-all hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isSearching ? 'rounded-l-none' : ''} ${enterPressed ? 'w-12' : ''}`}
              style={{
                minHeight: `${height}`,
                resize: 'none',
              }}
              onClick={() => pressSearch()}
              name="search-button"
              title={isHtmlContent ? 'Create article from HTML content' : 'Create new article'}
            >
              {isHtmlContent ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={'h-4 w-4 stroke-current' + (enterPressed ? ' hidden' : '')}
                >
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                  <polyline points="14,2 14,8 20,8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10,9 9,9 8,9"></polyline>
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={'h-4 w-4 stroke-current' + (enterPressed ? ' hidden' : '')}
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.3-4.3"></path>
                </svg>
              )}
            </button>
          }
        </div>
        {isDemo && !isSearching && <SearchDemoArticle locale={locale} setSearchTerm={setSearchTerm} />}
      </div>

      {/* Loading Modal */}
      {isLoadingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="mx-4 max-w-md rounded-2xl bg-white p-8 shadow-2xl dark:bg-gray-900">
            <div className="text-center">
              <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                {isHtmlContent ? 'Processing deep search content...' : 'Creating your article...'}
              </h3>
              <p className="mb-6 text-sm text-gray-600 dark:text-gray-300">
                {isHtmlContent
                  ? 'We are cleaning and formatting your HTML content, then creating the article. This may take a few moments.'
                  : 'Please wait while we generate your article. This may take a few moments.'}
              </p>
              <ThreeDotsLoading disableFullscreen />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export { SearchBar };
